import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, FileText, Calendar, AlertCircle, Users, TrendingUp,
  Clock, CheckCircle, XCircle, DollarSign, BarChart3, Bell, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import MetricCard from '../components/common/MetricCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusBadge from '../components/common/StatusBadge';

const CACSPartnerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalClients: 0,
    activeEngagements: 0,
    pendingCompliance: 0,
    upcomingDeadlines: 0
  });
  const [clients, setClients] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      // Simulate CA/CS Partner data - in production, this would fetch from backend
      setDashboardData({
        totalClients: 12,
        activeEngagements: 18,
        pendingCompliance: 5,
        upcomingDeadlines: 8
      });

      // Mock client data
      setClients([
        {
          id: '1',
          companyName: 'TechCorp India Ltd.',
          cin: 'U72200MH2015PTC123456',
          csrBudget: 50000000,
          status: 'active',
          lastActivity: '2024-01-20',
          complianceStatus: 'compliant',
          engagements: 2
        },
        {
          id: '2',
          companyName: 'Green Energy Solutions',
          cin: 'U40200GJ2018PTC234567',
          csrBudget: 35000000,
          status: 'active',
          lastActivity: '2024-01-18',
          complianceStatus: 'pending',
          engagements: 1
        },
        {
          id: '3',
          companyName: 'MediHealth Pharma',
          cin: 'U24230KA2012PTC345678',
          csrBudget: 80000000,
          status: 'active',
          lastActivity: '2024-01-22',
          complianceStatus: 'compliant',
          engagements: 3
        }
      ]);

      // Mock upcoming deadlines
      setUpcomingDeadlines([
        {
          id: '1',
          client: 'TechCorp India Ltd.',
          task: 'CSR-1 Form Filing',
          deadline: '2024-02-15',
          priority: 'high',
          status: 'pending'
        },
        {
          id: '2',
          client: 'Green Energy Solutions',
          task: 'Board Meeting for CSR Approval',
          deadline: '2024-02-10',
          priority: 'high',
          status: 'pending'
        },
        {
          id: '3',
          client: 'MediHealth Pharma',
          task: 'Quarterly CSR Report',
          deadline: '2024-02-28',
          priority: 'medium',
          status: 'in_progress'
        },
        {
          id: '4',
          client: 'TechCorp India Ltd.',
          task: 'Impact Assessment Report',
          deadline: '2024-03-15',
          priority: 'medium',
          status: 'pending'
        }
      ]);

      // Mock recent activity
      setRecentActivity([
        {
          id: '1',
          type: 'document',
          client: 'TechCorp India Ltd.',
          action: 'CSR Policy Updated',
          timestamp: '2 hours ago'
        },
        {
          id: '2',
          type: 'compliance',
          client: 'Green Energy Solutions',
          action: 'CSR-2 Form Submitted',
          timestamp: '5 hours ago'
        },
        {
          id: '3',
          type: 'meeting',
          client: 'MediHealth Pharma',
          action: 'CSR Committee Meeting Scheduled',
          timestamp: '1 day ago'
        }
      ]);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case 'compliant': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">CA/CS Partner Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage client compliance and advisory services</p>
            </div>
            <Button
              onClick={() => navigate('/clients/add')}
              className="text-white"
              style={{ background: '#14B8A6' }}
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Client
            </Button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Clients"
            value={dashboardData.totalClients}
            subtitle="Active partnerships"
            icon={Users}
            color="#14B8A6"
          />
          <MetricCard
            title="Active Engagements"
            value={dashboardData.activeEngagements}
            subtitle="Ongoing projects"
            icon={Briefcase}
            color="#3B82F6"
          />
          <MetricCard
            title="Pending Compliance"
            value={dashboardData.pendingCompliance}
            subtitle="Requires attention"
            icon={AlertCircle}
            color="#F59E0B"
          />
          <MetricCard
            title="Upcoming Deadlines"
            value={dashboardData.upcomingDeadlines}
            subtitle="Next 30 days"
            icon={Calendar}
            color="#EF4444"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Client List */}
          <div className="lg:col-span-2 space-y-6">
            {/* Clients */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Your Clients</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/clients')}
                >
                  View All
                </Button>
              </div>

              <div className="space-y-4">
                {clients.map(client => (
                  <div
                    key={client.id}
                    className="p-4 border border-gray-200 rounded-lg hover:border-teal-500 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/clients/${client.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {client.companyName}
                        </h3>
                        <p className="text-sm text-gray-600">CIN: {client.cin}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getComplianceColor(client.complianceStatus)}`}>
                        {client.complianceStatus}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-500">CSR Budget</p>
                        <p className="text-sm font-semibold text-gray-900">
                          ₹{(client.csrBudget / 10000000).toFixed(1)}Cr
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Engagements</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {client.engagements}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Last Activity</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {new Date(client.lastActivity).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/compliance/vault?client=${client.id}`);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-teal-600 border-teal-600"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Compliance
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clients/${client.id}/reports`);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 border-blue-600"
                      >
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Reports
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Activity</h2>
              <div className="space-y-4">
                {recentActivity.map(activity => (
                  <div key={activity.id} className="flex items-start gap-4 p-3 hover:bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      {activity.type === 'document' && <FileText className="w-5 h-5 text-teal-600" />}
                      {activity.type === 'compliance' && <CheckCircle className="w-5 h-5 text-teal-600" />}
                      {activity.type === 'meeting' && <Calendar className="w-5 h-5 text-teal-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                      <p className="text-xs text-gray-600">{activity.client}</p>
                      <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - Deadlines & Quick Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Upcoming Deadlines */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-teal-600" />
                  Deadlines
                </h3>
                <Bell className="w-5 h-5 text-gray-400" />
              </div>

              <div className="space-y-3">
                {upcomingDeadlines.slice(0, 5).map(deadline => (
                  <div key={deadline.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 line-clamp-1">
                        {deadline.task}
                      </h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(deadline.priority)}`}>
                        {deadline.priority}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">{deadline.client}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(deadline.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <span className={`text-xs font-medium ${deadline.status === 'pending' ? 'text-red-600' : 'text-yellow-600'}`}>
                        {deadline.status === 'pending' ? 'Pending' : 'In Progress'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => navigate('/compliance/calendar')}
                variant="outline"
                className="w-full mt-4 text-teal-600 border-teal-600"
              >
                View All Deadlines
              </Button>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Button
                  onClick={() => navigate('/consultancy/policy-builder')}
                  variant="outline"
                  className="w-full justify-start text-gray-700 border-gray-300"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Generate CSR Policy
                </Button>
                <Button
                  onClick={() => navigate('/compliance/vault')}
                  variant="outline"
                  className="w-full justify-start text-gray-700 border-gray-300"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Compliance Documents
                </Button>
                <Button
                  onClick={() => navigate('/impact/sroi-calculator')}
                  variant="outline"
                  className="w-full justify-start text-gray-700 border-gray-300"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  SROI Calculator
                </Button>
                <Button
                  onClick={() => navigate('/knowledge')}
                  variant="outline"
                  className="w-full justify-start text-gray-700 border-gray-300"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Knowledge Hub
                </Button>
              </div>
            </div>

            {/* Performance Summary */}
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">This Month</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Compliances Filed</span>
                  <span className="text-lg font-bold text-teal-600">12</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Reports Generated</span>
                  <span className="text-lg font-bold text-teal-600">8</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Meetings Held</span>
                  <span className="text-lg font-bold text-teal-600">15</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CACSPartnerDashboard;
